import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon,
  StarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/solid';

/* ── Brand icon SVGs ─────────────────────────────────────────────────────── */
const RedditIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/>
  </svg>
);

const TelegramIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const FacebookIcon = ({ className = '' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

interface ScanStats { source: string; total_posts: number; scan_count: number; last_scanned_at: string; }
interface PlatformScanStats { reddit: ScanStats[]; telegram: ScanStats[]; facebook: ScanStats[]; }

const DashboardHome: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [agentRunning, setAgentRunning] = useState(false);
  const [platformStats, setPlatformStats] = useState({ telegram_enabled: 0, facebook_enabled: 0 });
  const [scanStats, setScanStats] = useState<PlatformScanStats>({ reddit: [], telegram: [], facebook: [] });
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchAll();
    const t1 = setInterval(fetchStats, 8000);
    const t2 = setInterval(fetchAgentStatus, 8000);
    const t3 = setInterval(fetchScanStats, 8000);
    return () => { clearInterval(t1); clearInterval(t2); clearInterval(t3); };
  }, []);

  const fetchAll = () => { fetchStats(); fetchAgentStatus(); fetchPlatformStats(); fetchScanStats(); };

  const fetchStats = () =>
    fetch('/api/v1/leads/stats').then(r => r.json()).then(d => {
      setStats(d);
      setLoading(false);
    }).catch(() => setLoading(false));

  const fetchAgentStatus = () =>
    fetch('/api/v1/agents/status/lead_detection').then(r => r.json()).then(d => setAgentRunning(d.status === 'running')).catch(() => {});

  const fetchPlatformStats = () => {
    fetch('/api/v1/telegram-channels').then(r => r.json()).then(d => setPlatformStats(p => ({ ...p, telegram_enabled: d.enabled_count || 0 }))).catch(() => {});
    fetch('/api/v1/facebook-groups').then(r => r.json()).then(d => setPlatformStats(p => ({ ...p, facebook_enabled: d.enabled_count || 0 }))).catch(() => {});
  };

  const fetchScanStats = () =>
    fetch('/api/v1/scan-stats/all').then(r => r.json()).then(d => { if (d.success) setScanStats(d.data); }).catch(() => {});

  const handleRefresh = () => { setRefreshing(true); fetchAll(); setTimeout(() => setRefreshing(false), 1000); };
  const toggle = (k: string) => setExpanded(p => ({ ...p, [k]: !p[k] }));

  const totalPosts =
    scanStats.reddit.reduce((s, x) => s + x.total_posts, 0) +
    scanStats.telegram.reduce((s, x) => s + x.total_posts, 0) +
    scanStats.facebook.reduce((s, x) => s + x.total_posts, 0);

  const statCards = [
    { label: 'Total Leads',   value: stats?.total_leads ?? 0,                 icon: UserGroupIcon,       color: 'text-blue-600',    bg: 'bg-blue-50',    link: '/leads' },
    { label: 'Qualified',     value: stats?.by_classification?.qualified ?? 0, icon: StarIcon,            color: 'text-emerald-600', bg: 'bg-emerald-50', link: '/leads?classification=qualified' },
    { label: 'Posts Scanned', value: totalPosts,                               icon: ArrowTrendingUpIcon, color: 'text-violet-600',  bg: 'bg-violet-50',  link: null },
  ];

  const platforms = [
    {
      key: 'reddit',
      name: 'Reddit',
      Icon: RedditIcon,
      iconColor: 'text-orange-500',
      iconBg: 'bg-orange-50',
      active: true,
      sub: `${scanStats.reddit.length} subreddits · ${scanStats.reddit.reduce((s, x) => s + x.total_posts, 0).toLocaleString()} posts`,
      sources: scanStats.reddit,
      sourceLabel: (s: ScanStats) => `r/${s.source}`,
    },
    {
      key: 'telegram',
      name: 'Telegram',
      Icon: TelegramIcon,
      iconColor: 'text-sky-500',
      iconBg: 'bg-sky-50',
      active: platformStats.telegram_enabled > 0,
      sub: platformStats.telegram_enabled > 0
        ? `${scanStats.telegram.length} channels · ${scanStats.telegram.reduce((s, x) => s + x.total_posts, 0).toLocaleString()} posts`
        : 'No channels configured',
      sources: scanStats.telegram,
      sourceLabel: (s: ScanStats) => `@${s.source}`,
    },
    {
      key: 'facebook',
      name: 'Facebook',
      Icon: FacebookIcon,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-50',
      active: platformStats.facebook_enabled > 0,
      sub: platformStats.facebook_enabled > 0
        ? `${scanStats.facebook.length} groups · ${scanStats.facebook.reduce((s, x) => s + x.total_posts, 0).toLocaleString()} posts`
        : 'No groups configured',
      sources: scanStats.facebook,
      sourceLabel: (s: ScanStats) => s.source,
    },
  ];

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitoring extended-stay leads in real time</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Agent pill */}
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border ${agentRunning ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
            <span className={`h-2 w-2 rounded-full ${agentRunning ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`} />
            {agentRunning ? 'Agent running' : 'Agent stopped'}
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {statCards.map(card => (
          <div
            key={card.label}
            onClick={() => card.link && navigate(card.link)}
            className={`bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all ${card.link ? 'cursor-pointer hover:border-blue-200 hover:-translate-y-0.5' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{card.label}</p>
                <p className={`mt-2 text-3xl font-bold ${card.color}`}>{card.value.toLocaleString()}</p>
                {card.link && <p className="text-xs text-gray-400 mt-1">Click to view →</p>}
              </div>
              <div className={`${card.bg} p-2.5 rounded-xl`}>
                <card.icon className={`h-5 w-5 ${card.color}`} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Platform monitoring */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Platform Sources</h2>
            <p className="text-xs text-gray-500 mt-0.5">Live scanning activity</p>
          </div>
          <span className="text-xs text-gray-400">Refreshes every 8s</span>
        </div>

        <div className="divide-y divide-gray-50">
          {platforms.map(p => (
            <div key={p.key}>
              <button
                onClick={() => p.sources.length > 0 && toggle(p.key)}
                className={`w-full flex items-center gap-4 px-6 py-4 hover:bg-gray-50/60 transition-colors text-left ${p.sources.length > 0 ? 'cursor-pointer' : 'cursor-default'}`}
              >
                {/* Icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${p.iconBg} flex items-center justify-center`}>
                  <p.Icon className={`w-5 h-5 ${p.iconColor}`} />
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-800">{p.name}</span>
                    {p.active ? (
                      <CheckCircleIcon className="h-4 w-4 text-emerald-500" />
                    ) : (
                      <ExclamationCircleIcon className="h-4 w-4 text-amber-400" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{p.sub}</p>
                </div>

                {/* Status dot + chevron */}
                <div className="flex items-center gap-3 flex-shrink-0">
                  {agentRunning && p.active && (
                    <span className="flex h-2.5 w-2.5">
                      <span className="animate-ping absolute h-2.5 w-2.5 rounded-full bg-emerald-400 opacity-60" />
                      <span className="relative h-2.5 w-2.5 rounded-full bg-emerald-500" />
                    </span>
                  )}
                  {p.sources.length > 0 && (
                    <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${expanded[p.key] ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </button>

              {/* Expanded sources */}
              {expanded[p.key] && p.sources.length > 0 && (
                <div className="px-6 pb-4">
                  <div className="bg-gray-50 rounded-xl p-3 max-h-48 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-1.5">
                      {p.sources.map(s => (
                        <div key={s.source} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-gray-100">
                          <span className="text-xs font-medium text-gray-700 truncate">{p.sourceLabel(s)}</span>
                          <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{s.total_posts.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;
